import { configure, makeAutoObservable } from 'mobx';

import { AdminEventModel } from 'types/admin/trackmanEventsAdmin';
import { NotificationsStore } from '../../NotificationsStore';
import TrackmanEventsEditorService from 'services/Admin/Editors/TrackmanEventsEditorService';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { sortByString } from 'utilities/dataSorters';

configure({ enforceActions: 'never' });

export const cleanEventObject: AdminEventModel = {
  id: '',
  title: '',
  meetingUrl: '',
  locationType: 'Online',
  location: '',
  visible: false,
  premiumEvent: false,
  startDate: '',
  imageUrl: '',
  recordingReady: false,
};

export class TrackmanEventsEditorStore {
  private trackmanEventsEditorService: TrackmanEventsEditorService;
  private ns: NotificationsStore;

  constructor(trackmanEventsEditorService: TrackmanEventsEditorService, notificationStore: NotificationsStore) {
    makeAutoObservable(this); //This line will automatically decorate each store property with 'observable' and each method with 'action'.
    this.trackmanEventsEditorService = trackmanEventsEditorService;
    this.ns = notificationStore;
  }

  eventsList: AdminEventModel[] = [];
  filteredList: AdminEventModel[] = [];
  eventItem: AdminEventModel = { ...cleanEventObject };
  selectedEventId: string = '';
  imagePreview: string = '';
  uploadedImageAccepted: boolean = false;
  isVideoIdSelected: boolean = false;
  isSelectedTime: boolean = false;

  isLoading: boolean = false;
  isSaving: boolean = false;

  searchValue: string = '';
  setSearchValue = (value: string) => (this.searchValue = value);

  getEvents = async () => {
    try {
      this.isLoading = true;
      const response = await this.trackmanEventsEditorService.getEvents();
      this.eventsList = response.events;
      this.filteredList = sortByString(response.events, 'title', 'ASCENDING');
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.isLoading = false;
  };

  getEvent = async (id: string) => {
    try {
      this.isLoading = true;
      const response = await this.trackmanEventsEditorService.getEvent(id);
      this.eventItem = response;
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.isLoading = false;
  };

  deleteEventAndUpdate = async () => {
    try {
      this.isLoading = true;
      await this.trackmanEventsEditorService.deleteEvent(this.selectedEventId);
      this.getEvents();
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.isLoading = false;
  };

  saveNewEventAndUpdate = async () => {
    try {
      this.isSaving = true;
      delete this.eventItem.id;
      delete this.eventItem.videoUrl;
      await this.trackmanEventsEditorService.saveNewEvent(this.eventItem);
      this.ns.addToast('success', 'New event has been saved successfully', 'success');
      this.getEvents();
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.eventItem = structuredClone(cleanEventObject);
    this.isSaving = false;
  };

  saveExistingEventAndUpdate = async () => {
    try {
      this.isSaving = true;

      await this.trackmanEventsEditorService.saveExistingEvent(this.eventItem);
      this.ns.addToast('success', 'This event has been updated successfully', 'success');
      this.getEvents();
    } catch (ex) {
      this.ns.addToast('error', getErrorMessage(ex), 'error');
    }
    this.isSaving = false;
  };
}

export default TrackmanEventsEditorStore;
